import type { FlightCardData } from '@massilia/queries'

import { Button } from '@massilia/ui'
import { RotateCcw } from 'lucide-react'
import { useMemo } from 'react'

import { type AirlineOption, CompanyFilter } from './company-filter'
import { DurationFilter } from './duration-filter'
import { boundsToRange, RESET_FILTERS_PATCH } from './filter-url-params'
import { getDurationBounds, getFilterAirline, getPriceBounds } from './filter-utils'
import { useFiltersState } from './filters-state-context'
import { LuggageFilter } from './luggage-filter'
import { PriceFilter } from './price-filter'
import { StopsFilter } from './stops-filter'
import { TimeFilter } from './time-filter'

interface FlightFiltersProps {
  offers: FlightCardData[]
}

export function FlightFilters({ offers }: FlightFiltersProps) {
  const { filters, setFilters } = useFiltersState()

  const priceBounds = useMemo(() => getPriceBounds(offers), [offers])
  const durationBounds = useMemo(() => getDurationBounds(offers), [offers])
  const availableAirlines = useMemo((): AirlineOption[] => {
    const airlinesMap = new Map<string, string>()
    offers.forEach((offer) => {
      const { name, code } = getFilterAirline(offer)
      if (code && !airlinesMap.has(code)) {
        airlinesMap.set(code, name)
      }
    })
    return Array.from(airlinesMap.entries())
      .map(([code, name]) => ({ name, code }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [offers])

  const hasActiveFilters = useMemo(() => {
    // Un intervalle est actif dès qu'il RESSERRE la plage réelle. Deux bugs
    // corrigés ici : exiger les deux bornes laissait le bouton « Réinitialiser »
    // grisé alors qu'un plafond posé par le chat filtrait bel et bien ; et la
    // comparaison croisée (`max > bounds.min || min < bounds.max`) était vraie
    // dans presque tous les cas, y compris sans aucun filtre.
    const priceRange = boundsToRange(filters.filterPriceMin, filters.filterPriceMax)
    const isPriceFilterActive =
      !!priceRange && (priceRange.min > priceBounds.min || priceRange.max < priceBounds.max)

    const durationRange = boundsToRange(filters.filterDurationMin, filters.filterDurationMax)
    const isDurationFilterActive =
      !!durationRange &&
      (durationRange.min > durationBounds.min || durationRange.max < durationBounds.max)
    const isTimeFilterActive = filters.filterTimeRanges
      ? filters.filterTimeRanges.length > 0
      : false
    const isAirlinesFilterActive = filters.filterAirlines
      ? filters.filterAirlines.length > 0
      : false

    return (
      isPriceFilterActive ||
      isDurationFilterActive ||
      isTimeFilterActive ||
      isAirlinesFilterActive ||
      filters.filterStops !== 'all' ||
      filters.filterLuggage !== '1'
    )
  }, [filters, priceBounds, durationBounds])

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">Filtres</h2>

      <div className="space-y-0">
        <CompanyFilter airlines={availableAirlines} offers={offers} />
        <LuggageFilter />
        <StopsFilter />
        <PriceFilter bounds={priceBounds} />
        <DurationFilter bounds={durationBounds} />
        <TimeFilter offers={offers} />
      </div>

      {/* Reset button */}
      <div className="flex flex-row justify-between border-t border-gray-200 pt-4">
        <Button
          className="w-full"
          disabled={!hasActiveFilters}
          onClick={() => {
            setFilters(RESET_FILTERS_PATCH)
          }}
          variant="outline"
        >
          <RotateCcw className="ml-2 h-4 w-4" />
          <span className="mr-2 py-2 text-sm font-medium text-wrap text-gray-900">
            Réinitialiser les filtres
          </span>
        </Button>
      </div>
    </div>
  )
}
